import { useState, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { XIcon } from "lucide-react"
import { api } from "@/lib/api"

type DriverStatus = "active" | "sick" | "on_vacation" | "art" | "suspended"

interface FormState {
  phoneCdc: string
  phonePersonal: string
  code: string
  name: string
  fileId: string
  dni: string
  docTypeId: string
  docId: string
  isOwned: boolean
  pvcuenta: string
  operatorId: string
  driverStatus: DriverStatus
}

const EMPTY_FORM: FormState = {
  code: "",
  name: "",
  fileId: "",
  dni: "",
  docTypeId: "",
  docId: "",
  isOwned: false,
  pvcuenta: "",
  operatorId: "",
  driverStatus: "active",
  phoneCdc: "",
  phonePersonal: "",
}

interface Props {
  open: boolean
  onClose: () => void
  editing: any | null
  onSave: (id: string | null, data: any) => void
  isSaving: boolean
  error: string | null
}

export function DriverFormSheet({ open, onClose, editing, onSave, isSaving, error }: Props) {
  const [form, setForm]         = useState<FormState>(EMPTY_FORM)
  const [sgtCodes, setSgtCodes] = useState<string[]>([])
  const [sgtInput, setSgtInput] = useState("")

  const { data: operatorOptions = [] } = useQuery({
    queryKey: ["users-operators"],
    queryFn: async () => {
      const res = await api.users.$get({ query: {} })
      if (!res.ok) return []
      const all = await res.json() as { id: string; firstName: string; lastName: string; role: string }[]
      return all.filter(u => u.role === "operator" || u.role === "fleet_manager")
    },
    staleTime: 60000,
  })

  const { data: docTypeOptions = [] } = useQuery({
    queryKey: ["doc-types"],
    queryFn: async () => {
      const res = await api["doc-types"].$get({ query: {} })
      if (!res.ok) return []
      return res.json() as Promise<{ id: string; code: string; name: string }[]>
    },
    staleTime: 60000,
  })

  useEffect(() => {
    if (editing) {
      setForm({
        code: editing.code ?? "",
        name: editing.name ?? "",
        fileId: editing.fileId != null ? String(editing.fileId) : "",
        dni: editing.dni ?? "",
        docTypeId: editing.docTypeId ?? "",
        docId: editing.docId ?? "",
        isOwned: editing.isOwned ?? false,
        pvcuenta: editing.pvcuenta != null ? String(editing.pvcuenta) : "",
        operatorId: editing.operatorId ?? "",
        driverStatus: editing.driverStatus ?? "active",
        phoneCdc: editing.phoneCdc ?? "",
        phonePersonal: editing.phonePersonal ?? "",
      })
      setSgtCodes(editing.sgtCodes ?? [])
    } else {
      setForm(EMPTY_FORM)
      setSgtCodes([])
    }
    setSgtInput("")
  }, [editing, open])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSave(editing?.id ?? null, {
      ...form,
      sgtCodes: sgtCodes.length > 0 ? sgtCodes : null,
    })
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto flex flex-col">
        <SheetHeader>
          <SheetTitle>{editing ? "Editar chofer" : "Nuevo chofer"}</SheetTitle>
          <SheetDescription>Información del conductor para asignación de viajes.</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="drv-code">Código</Label>
              <Input
                id="drv-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                maxLength={10}
                className="uppercase font-mono"
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="drv-name">Nombre completo</Label>
              <Input
                id="drv-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Códigos SGT</Label>
            <div className="flex flex-wrap gap-1.5 items-center min-h-9 rounded-md border border-input px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
              {sgtCodes.map((code, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-muted font-mono text-xs px-2 py-0.5 rounded-sm">
                  {code}
                  <button type="button" onClick={() => setSgtCodes(sgtCodes.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground">
                    <XIcon className="size-3" />
                  </button>
                </span>
              ))}
              <input
                value={sgtInput}
                onChange={(e) => setSgtInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault()
                    const code = sgtInput.toUpperCase().trim()
                    if (code && !sgtCodes.includes(code)) setSgtCodes([...sgtCodes, code])
                    setSgtInput("")
                  }
                }}
                placeholder={sgtCodes.length === 0 ? "Escribir y Enter..." : ""}
                className="flex-1 min-w-28 bg-transparent text-xs font-mono outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>DNI</Label>
              <Input value={form.dni} onChange={e => setForm(f => ({ ...f, dni: e.target.value }))} />
            </div>
            <div className="grid gap-1.5">
              <Label>Legajo (fileId)</Label>
              <Input value={form.fileId} onChange={e => setForm(f => ({ ...f, fileId: e.target.value }))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Tel. CDC</Label>
              <Input value={form.phoneCdc} onChange={e => setForm(f => ({ ...f, phoneCdc: e.target.value }))} />
            </div>
            <div className="grid gap-1.5">
              <Label>Tel. Personal</Label>
              <Input value={form.phonePersonal} onChange={e => setForm(f => ({ ...f, phonePersonal: e.target.value }))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
                <Label>Tipo Doc.</Label>
                <Select value={form.docTypeId} onValueChange={(v: string | null) => setForm(f => ({ ...f, docTypeId: v ?? "" }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar...">
                      {docTypeOptions.find(t => t.id === form.docTypeId)?.name ?? null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {docTypeOptions.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            <div className="grid gap-1.5">
              <Label>Nro Doc.</Label>
              <Input value={form.docId} onChange={e => setForm(f => ({ ...f, docId: e.target.value }))} />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="drv-pvcuenta">PV Cuenta <span className="text-destructive">*</span></Label>
            <Input
              id="drv-pvcuenta"
              type="number"
              min={1}
              step={1}
              value={form.pvcuenta}
              onChange={e => setForm(f => ({ ...f, pvcuenta: e.target.value }))}
              required
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Operador Asignado</Label>
            <Select
              value={form.operatorId || "__none__"}
              onValueChange={(v) => setForm(f => ({ ...f, operatorId: v === "__none__" ? "" : (v ?? "") }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sin operador">
                  {operatorOptions.find(o => o.id === form.operatorId)
                    ? `${operatorOptions.find(o => o.id === form.operatorId)!.firstName} ${operatorOptions.find(o => o.id === form.operatorId)!.lastName}`
                    : "Sin operador"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin operador</SelectItem>
                {operatorOptions.map(o => (
                  <SelectItem key={o.id} value={o.id}>{o.firstName} {o.lastName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label>Estado del chofer</Label>
            <Select
              value={form.driverStatus}
              onValueChange={(v) => setForm(f => ({ ...f, driverStatus: v as DriverStatus }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="sick">Enfermo</SelectItem>
                <SelectItem value="on_vacation">De vacaciones</SelectItem>
                <SelectItem value="art">Con ART</SelectItem>
                <SelectItem value="suspended">Suspendido</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2 border rounded-md p-3">
            <Checkbox
              id="is-owned"
              checked={form.isOwned}
              onCheckedChange={(checked) => setForm(f => ({ ...f, isOwned: !!checked }))}
            />
            <div className="grid gap-1.5 leading-none">
              <label htmlFor="is-owned" className="text-sm font-medium leading-none cursor-pointer">Chofer propio</label>
              <p className="text-xs text-muted-foreground">Marcar si el chofer pertenece a la flota propia (CDC).</p>
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
          )}

          <SheetFooter className="px-0 mt-auto pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear chofer"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
